import React, { useEffect, useState, useContext, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { processWithdrawalAction } from "../api";
import { ThemeContext } from "../App";

export default function WithdrawalConfirmation() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, confirming, success, error, expired, already_processed
  const [message, setMessage] = useState("");
  const [details, setDetails] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { theme } = useContext(ThemeContext);

  const token = searchParams.get("token");
  const action = searchParams.get("action");

  const styles = useMemo(() => {
    const isDark = theme === "dark";
    return {
      base: isDark ? "bg-slate-900 text-white" : "bg-slate-50 text-gray-900",
      card: isDark
        ? "bg-gray-800 text-white border border-gray-700"
        : "bg-white text-gray-900 border border-gray-200",
      muted: isDark ? "text-gray-400" : "text-gray-500",
      success: "text-emerald-500",
      error: "text-red-500",
      warning: "text-yellow-500",
    };
  }, [theme]);

  useEffect(() => {
    if (!token || !action) {
      setStatus("error");
      setMessage("Invalid link. Missing token or action parameter.");
      return;
    }

    if (action !== "approve" && action !== "reject") {
      setStatus("error");
      setMessage("Invalid action. Must be 'approve' or 'reject'.");
      return;
    }

    // Show confirmation dialog before processing
    setStatus("confirming");
    setShowConfirmDialog(true);
  }, [token, action]);

  const handleConfirm = async () => {
    setShowConfirmDialog(false);
    setStatus("loading");
    setMessage(
      action === "approve"
        ? "Processing your approval..."
        : "Processing your rejection..."
    );

    try {
      const result = await processWithdrawalAction(token, action);

      if (result.success) {
        setStatus("success");
        setMessage(result.message);
        setDetails(result);
      } else {
        // Handle specific statuses
        if (
          result.status === "expired" ||
          result.status === "approved" ||
          result.status === "rejected" ||
          result.status === "cancelled"
        ) {
          setStatus("already_processed");
          setMessage(result.message);
          setDetails(result);
        } else if (result.status === "insufficient_balance") {
          setStatus("error");
          setMessage(result.message);
          setDetails(result);
        } else {
          setStatus("error");
          setMessage(result.message || "An error occurred.");
          setDetails(result);
        }
      }
    } catch (err) {
      console.error("Failed to process withdrawal action:", err);
      setStatus("error");
      if (err.response?.data?.detail) {
        setMessage(err.response.data.detail);
      } else if (err.response?.data?.message) {
        setMessage(err.response.data.message);
      } else {
        setMessage("Unable to process your request. Please try again later.");
      }
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    setStatus("cancelled");
    setMessage("You cancelled the action. No changes have been made.");
  };

  const renderIcon = () => {
    switch (status) {
      case "loading":
        return (
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        );
      case "success":
        return (
          <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        );
      case "error":
        return (
          <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        );
      case "already_processed":
      case "expired":
        return (
          <div className="w-16 h-16 rounded-full bg-yellow-500 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const renderConfirmDialog = () => {
    if (!showConfirmDialog) return null;

    const isApproval = action === "approve";

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div
          className={`max-w-md w-full p-6 rounded-lg shadow-xl ${styles.card}`}
        >
          <h3 className="text-xl font-bold mb-4">
            {isApproval
              ? "Confirm Withdrawal Approval"
              : "Confirm Withdrawal Rejection"}
          </h3>
          <p className={`mb-6 ${styles.muted}`}>
            {isApproval
              ? "Are you sure you want to approve this withdrawal? The amount will be deducted from your account balance."
              : "Are you sure you want to reject this withdrawal? No funds will be deducted from your account."}
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={handleCancel}
              className="px-4 py-2 rounded border border-gray-500 hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className={`px-4 py-2 rounded text-white transition-colors ${
                isApproval
                  ? "bg-emerald-600 hover:bg-emerald-500"
                  : "bg-red-600 hover:bg-red-500"
              }`}
            >
              {isApproval ? "Yes, Approve" : "Yes, Reject"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 ${styles.base}`}
    >
      {renderConfirmDialog()}
      <div
        className={`max-w-lg w-full p-8 rounded-lg shadow-xl ${styles.card}`}
      >
        <div className="text-center">
          {renderIcon()}

          <h1 className="text-2xl font-bold mb-2">
            {status === "loading" && "Processing..."}
            {status === "confirming" &&
              (action === "approve"
                ? "Approve Withdrawal"
                : "Reject Withdrawal")}
            {status === "success" &&
              (action === "approve"
                ? "Withdrawal Approved"
                : "Withdrawal Rejected")}
            {status === "error" && "Error"}
            {status === "already_processed" && "Already Processed"}
            {status === "cancelled" && "Action Cancelled"}
          </h1>

          <p className={`mb-6 ${styles.muted}`}>{message}</p>

          {status === "success" && details && (
            <div
              className={`text-left p-4 rounded-lg bg-opacity-10 mb-6 ${
                action === "approve" ? "bg-emerald-500" : "bg-gray-500"
              }`}
            >
              {details.new_balance !== undefined && (
                <p className="mb-2">
                  <span className={styles.muted}>New Balance:</span>{" "}
                  <span className="font-semibold">
                    $
                    {details.new_balance?.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </p>
              )}
              {details.balance !== undefined && (
                <p className="mb-2">
                  <span className={styles.muted}>Current Balance:</span>{" "}
                  <span className="font-semibold">
                    $
                    {details.balance?.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </p>
              )}
            </div>
          )}

          <div className="mt-6">
            <Link
              to="/"
              className="inline-block px-6 py-2 rounded bg-blue-600 hover:bg-blue-500 text-white transition-colors"
            >
              Go to Login
            </Link>
          </div>

          <p className={`mt-6 text-xs ${styles.muted}`}>
            If you have any questions, please contact support.
          </p>
        </div>
      </div>
    </div>
  );
}
